'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import useRadioGroupState from '@haaretz/s-use-radio-group-state';
import * as React from 'react';
import s9 from 'style9';
const transitionDuration = '0.25s';
const outlineTransitionTime = '0.1s';
// `c` is short for `classNames`
const c = s9.create({
    wrapper: {
        '--bgc': "rgb(var(--c-neutral100))",
        alignItems: 'center',
        columnGap: "0.5rem",
        cursor: 'pointer',
        display: 'inline-flex',
        position: 'relative',
        ':hover': {
            '--bgc': "rgb(var(--c-primary400))"
        },
        ':focus-within': {
            '--bgc': "rgb(var(--c-primary400))",
            '--outline-wdth': '2px',
            '--outline-offst': '3px'
        }
    },
    input: {
        pointerEvents: 'none',
        touchAction: 'none',
        height: 0,
        opacity: 0,
        // We need this so that the input isn't considered for
        // creating gaps in the wrapper
        position: 'absolute',
        width: 0
    },
    radioButton: {
        aspectRatio: '1',
        backgroundColor: "rgb(var(--c-primary1000))",
        borderRadius: "clamp(0px, ((100vw - 4px) - 100%) * 9999, 50%) / 50%",
        flexGrow: 0,
        flexShrink: 0,
        outlineColor: "rgba(var(--c-primary1000),0.5)",
        outlineOffset: 'var(--outline-offst, 0)',
        outlineStyle: 'solid',
        outlineWidth: 'var(--outline-wdth, 0)',
        position: 'relative',
        transitionDuration: outlineTransitionTime,
        transitionProperty: 'all',
        transitionTimingFunction: 'ease-in-out',
        width: "1rem",
        "@media all and (min-width:80em)": {
            width: "1.25rem"
        },
        ':after': {
            backgroundColor: 'var(--bgc)',
            borderRadius: "clamp(0px, ((100vw - 4px) - 100%) * 9999, 50%) / 50%",
            content: '""',
            height: 'calc(100% - 2px)',
            left: '50%',
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            transitionDuration: `${transitionDuration}, ${transitionDuration}`,
            transitionProperty: 'height, width',
            transitionTimingFunction: 'ease-in-out',
            width: 'calc(100% - 2px)'
        }
    },
    disabledStateButton: {
        opacity: 0.25
    },
    disabledStateText: {
        opacity: 0.5
    },
    radioButtonChecked: {
        ':after': {
            height: "0.5rem",
            width: "0.5rem"
        }
    },
    disabled: {
        cursor: 'no-drop'
    },
    label: {
        color: "rgb(var(--c-bodyText))",
        fontSize: "var(--fz--2)",
        lineHeight: "var(--lh--2)",
        "@media all and (min-width:102em)": {
            fontSize: "var(--fz--3)",
            lineHeight: "var(--lh--3)"
        }
    }
});
export function RadioButtonWrapper({ ref, children, inlineStyle, inputRef, styleExtend = [], onClick, onKeyDown, state, ...attrs }) {
    const isDisabled = state === 'disabled';
    return(// eslint-disable-next-line max-len
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
    /*#__PURE__*/ _jsx("label", {
        ...attrs,
        className: s9(c.wrapper, isDisabled && c.disabled, ...styleExtend),
        style: inlineStyle,
        onClick: onClick,
        onKeyDown: onKeyDown,
        ref: ref,
        children: children
    }));
}
export function RadioButtonText({ ref, id, children, state, textStyleExtend = [], ...attrs }) {
    const isDisabled = state === 'disabled';
    return /*#__PURE__*/ _jsx("span", {
        ...attrs,
        className: s9(c.label, isDisabled && c.disabledStateText, ...textStyleExtend),
        id: id,
        ref: ref,
        children: children
    });
}
export function FauxRadioButton({ ref, name, defaultChecked, labelledBy, isRequired, state, value, circleStyleExtend = [], ...attrs }) {
    const inputRef = React.useRef(null);
    React.useImperativeHandle(ref, ()=>inputRef.current);
    const isDisabled = state === 'disabled';
    const isChecked = !!attrs.checked;
    const attrsOverride = getAttrsOverride({
        labelledBy,
        state
    });
    if (!name) {
        console.error('<RadioButton>: the `name` prop is required for a radio button to work as expected. Either pass it directly or use a `<ButtonGrouop>`');
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("input", {
                name: name,
                ...attrs,
                ...attrsOverride,
                required: isRequired,
                className: s9(c.input),
                defaultChecked: defaultChecked,
                disabled: isDisabled,
                value: value,
                ref: inputRef
            }),
            /*#__PURE__*/ _jsx("div", {
                "aria-hidden": "true",
                className: `${s9(c.radioButton, isChecked && c.radioButtonChecked, isDisabled && c.disabled, isDisabled && c.disabledStateButton, ...circleStyleExtend)} ${attrs.className || ''}`
            })
        ]
    });
}
export default function RadioButton({ ref, inlineStyle, checked, children, styleExtend = [], textStyleExtend = [], circleStyleExtend, isRequired: isRequiredProp, state: stateProp, ...attrs }) {
    const radioButtonId = React.useId();
    const labelId = React.useId();
    const inputRef = React.useRef(null);
    const { checkedElement, firstRadioButton, isChecked, isRequired, name, onChange, tabIndex, state: stateFromGroup } = useRadioGroupState({
        checked,
        defaultChecked: attrs.defaultChecked,
        isRequired: isRequiredProp,
        input: inputRef.current
    });
    const state = stateProp || stateFromGroup;
    const stateForSubComponents = state === 'disabled' ? 'disabled' : undefined;
    const wrapperOnClick = React.useCallback((evt)=>{
        if (state === 'read-only') {
            // focus focusable radio button
            isChecked ? inputRef.current?.focus() : checkedElement ? checkedElement.focus() : firstRadioButton ? firstRadioButton.focus() : inputRef.current?.focus();
        }
        // Nothing further should happen if the element is disabled read-only
        if ([
            'disabled',
            'read-only'
        ].includes(state || '')) evt.preventDefault();
    }, [
        checkedElement,
        firstRadioButton,
        isChecked,
        state
    ]);
    const wrapperOnKeyDown = React.useCallback((evt)=>{
        const isMovementKey = [
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown'
        ].includes(evt.key);
        const isSelectionKey = [
            ' ',
            'Enter',
            'Spacebar'
        ].includes(evt.key);
        // prevent selection changes when read-only
        if (state === 'read-only' && (isMovementKey || isSelectionKey)) evt.preventDefault();
    }, [
        state
    ]);
    return /*#__PURE__*/ _jsxs(RadioButtonWrapper, {
        style: inlineStyle,
        styleExtend: styleExtend,
        inputRef: ref || inputRef,
        onClick: wrapperOnClick,
        onKeyDown: wrapperOnKeyDown,
        state: stateForSubComponents,
        children: [
            /*#__PURE__*/ _jsx(FauxRadioButton, {
                ...attrs,
                checked: isChecked,
                isRequired: isRequired,
                name: attrs.name || name,
                onChange: onChange,
                tabIndex: tabIndex,
                id: radioButtonId,
                labelledBy: labelId,
                state: stateForSubComponents,
                ref: ref || inputRef,
                circleStyleExtend: circleStyleExtend
            }),
            /*#__PURE__*/ _jsx(RadioButtonText, {
                id: labelId,
                state: stateForSubComponents,
                textStyleExtend: textStyleExtend,
                children: children
            })
        ]
    });
}
function getAttrsOverride({ labelledBy, state }) {
    const attrsOverride = {
        type: 'radio'
    };
    if (labelledBy) attrsOverride['aria-labelledby'] = labelledBy;
    if (state === 'disabled') {
        attrsOverride.tabIndex = undefined;
        attrsOverride.disabled = true;
    }
    return attrsOverride;
}
const colorCssVariablesHtz = [
    "neutral100",
    "primary400",
    "primary1000",
    "bodyText"
];
const lineHeightCssVariablesHtz = [
    "--lh--2",
    "--lh--3"
];
const fontSizeCssVariablesHtz = [
    "--fz--2",
    "--fz--3"
];

//# sourceMappingURL=RadioButton.js.map